import React, { useState } from 'react';
import { AlertTriangle, X, ChevronDown } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';

/**
 * DuplicateDialog — Popup vor dem Anlegen, wenn ähnliche Materialien existieren.
 * Zeigt Quelle, Score (≤100 %), WARUM-Chips (matchende Kriterien) und klappt je
 * Treffer die Kerndaten inline auf (kein Verlassen der Erfassung).
 *
 * Props: candidates [{source, ref, matnr, text, matkl, mtart, meins, werks, ean11, status, score, reasons[]}],
 *        onConfirm(), onCancel()
 */
const DuplicateDialog = ({ candidates = [], onConfirm, onCancel }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [open, setOpen] = useState(-1); // aufgeklappter Treffer-Index

  const panel = isDarkMode ? 'bg-gray-900 border border-white/10' : 'bg-white border border-slate-200';

  const sourceMeta = (s) => {
    if (s === 'sap') return { label: t('material.dupSourceSap'), cls: isDarkMode ? 'bg-blue-500/15 text-blue-300' : 'bg-blue-50 text-blue-700' };
    if (s === 'catalog') return { label: t('material.dupSourceCatalog'), cls: isDarkMode ? 'bg-violet-500/15 text-violet-300' : 'bg-violet-50 text-violet-700' };
    return { label: t('material.dupSourceMaterial'), cls: isDarkMode ? 'bg-emerald-500/15 text-emerald-300' : 'bg-emerald-50 text-emerald-700' };
  };
  const reasonLabel = (r) => t(`material.dupReason_${r}`);
  const scorePct = (s) => Math.min(100, Math.round((s || 0) * 100));

  const detailRow = (label, val) => (val == null || val === '') ? null : (
    <div className="flex flex-col">
      <span className={`text-[10px] uppercase ${isDarkMode ? 'text-white/30' : 'text-slate-400'}`}>{label}</span>
      <span className={`text-sm ${isDarkMode ? 'text-white/85' : 'text-slate-700'}`}>{val}</span>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onMouseDown={onCancel}>
      <div className={`w-full max-w-lg rounded-2xl shadow-2xl ${panel}`} onMouseDown={(e) => e.stopPropagation()}>
        <div className={`flex items-start gap-3 p-5 border-b ${isDarkMode ? 'border-white/10' : 'border-slate-100'}`}>
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isDarkMode ? 'bg-amber-500/15' : 'bg-amber-50'}`}>
            <AlertTriangle className="w-5 h-5 text-amber-500" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{t('material.dupTitle')}</h3>
            <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>{t('material.dupSubtitle')}</p>
          </div>
          <button type="button" onClick={onCancel} className={`p-1 rounded-lg ${isDarkMode ? 'text-white/50 hover:bg-white/10' : 'text-slate-400 hover:bg-slate-100'}`}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="max-h-80 overflow-y-auto p-3 space-y-2">
          {candidates.map((c, i) => {
            const sm = sourceMeta(c.source);
            const isOpen = open === i;
            return (
              <div key={`${c.source}-${c.id}-${i}`} className={`rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-slate-50'}`}>
                <button type="button" onClick={() => setOpen(isOpen ? -1 : i)} className="w-full text-left px-3 py-2">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${sm.cls}`}>{sm.label}</span>
                    <span className={`font-mono text-xs ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>{c.ref}</span>
                    <span className={`ml-auto text-[11px] font-medium ${c.score >= 1 ? 'text-amber-500' : (isDarkMode ? 'text-white/40' : 'text-slate-400')}`}>
                      {t('material.dupMatch')}: {scorePct(c.score)}%
                    </span>
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''} ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`} />
                  </div>
                  <div className={`text-sm ${isDarkMode ? 'text-white/90' : 'text-slate-800'}`}>{c.text || '—'}</div>
                  {/* WARUM-Chips */}
                  {Array.isArray(c.reasons) && c.reasons.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {c.reasons.map((r) => (
                        <span key={r} className={`inline-flex px-1.5 py-0.5 rounded text-[10px] ${isDarkMode ? 'bg-white/10 text-white/60' : 'bg-slate-200/70 text-slate-600'}`}>
                          {reasonLabel(r)}
                        </span>
                      ))}
                    </div>
                  )}
                </button>
                {isOpen && (
                  <div className={`px-3 pb-3 pt-1 grid grid-cols-2 sm:grid-cols-3 gap-2 border-t ${isDarkMode ? 'border-white/10' : 'border-slate-200/70'}`}>
                    {detailRow(t('material.matnr'), c.matnr || c.ref)}
                    {detailRow(t('material.mtart'), c.mtart)}
                    {detailRow(t('material.matkl'), c.matkl)}
                    {detailRow('EAN', c.ean11)}
                    {detailRow(t('material.werks'), c.werks)}
                    {detailRow(t('material.statusCol'), c.status ? t(`material.status_${c.status}`) : null)}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className={`flex items-center justify-end gap-3 p-4 border-t ${isDarkMode ? 'border-white/10' : 'border-slate-100'}`}>
          <button type="button" onClick={onCancel}
            className={`px-4 py-2 rounded-xl text-sm font-medium ${isDarkMode ? 'bg-white/10 hover:bg-white/15 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'}`}>
            {t('material.dupCancel')}
          </button>
          <button type="button" onClick={onConfirm}
            className="px-4 py-2 rounded-xl text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700">
            {t('material.dupProceed')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DuplicateDialog;
